import React from 'react';
import { Tooltip } from '@clockout-dev/design-elements';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { ENROLLMENT_COPY, ENROLLMENT_VARIANTS } from '../user.constants';
import styles from './styles/user-header.module.scss';

/**
 * The enrollment part of the header's meta line (KAN-738).
 *
 * Five looks, deliberately distinct:
 *  - SAME_DAY / DIFFERENT_DAYS: plain tertiary text.
 *  - NOT_COMPLETED: the trailing "Enrollment not completed" in amber.
 *  - LOADING: a skeleton bar with no words, so it cannot be read as a state.
 *  - UNAVAILABLE: grey text + info icon whose tooltip says the read failed.
 *    Never amber — a failed read says nothing about the member.
 *
 * @param {Object} props
 * @param {{ variant: string, lead: string | null, warning: string | null }} props.chip
 *   From `UserHeaderController.buildEnrollmentChip` — the text is final here.
 * @param {string} [props.className] Meta-item class from the parent.
 */
function EnrollmentChip({ chip, className }) {
    if (!chip || chip.variant === ENROLLMENT_VARIANTS.HIDDEN) {
        return null;
    }

    if (chip.variant === ENROLLMENT_VARIANTS.LOADING) {
        return (
            <span className={className} data-testid="enrollment-chip" data-variant={chip.variant}>
                <span className={styles.skeleton} role="status" aria-label={ENROLLMENT_COPY.LOADING_LABEL} />
            </span>
        );
    }

    if (!chip.lead) {
        return null;
    }

    if (chip.variant === ENROLLMENT_VARIANTS.UNAVAILABLE) {
        return (
            <span className={className} data-testid="enrollment-chip" data-variant={chip.variant}>
                <Tooltip title={ENROLLMENT_COPY.UNAVAILABLE_TOOLTIP} trigger={['hover', 'focus']}>
                    <span className={styles.unavailable} tabIndex={0}>
                        {chip.lead}
                        <InformationCircleIcon className={styles.inlineIcon} aria-hidden="true" />
                    </span>
                </Tooltip>
            </span>
        );
    }

    return (
        <span className={className} data-testid="enrollment-chip" data-variant={chip.variant}>
            {chip.lead}
            {chip.warning ? <span className={styles.warning}>{chip.warning}</span> : null}
        </span>
    );
}

export default EnrollmentChip;
